import { z } from "zod";

import { PublicCapabilityStatusSchema } from "./zcapability-status";

/**
 * The refusal a request gets when it reaches a route whose capability is off.
 *
 * ## Why it lives beside the status contract rather than in the backend
 *
 * `/api/capabilities` publishes the registry's rows; this is what one of those
 * rows looks like when a caller meets it head-on. They are the same vocabulary
 * seen from two sides, so the `capability` field below is declared AS
 * {@link PublicCapabilityStatusSchema}'s `name` rather than as a fresh
 * `z.string()`. A constraint landing on one — a kebab-case pin, a length bound —
 * lands on both, which is the entire reason to reuse a shape instead of matching
 * one by eye.
 *
 * ## 🔴 NOTHING HUMAN-READABLE CROSSES THIS BOUNDARY
 *
 * The backend states the machine fact; the frontend decides the sentence and the
 * language. i18n is the client's, and a backend that ships display copy has
 * already taken that decision away from it — quietly, because the copy renders
 * perfectly and nobody sees the choice being made.
 *
 * So the two string-typed slots are both pinned to {@link CAPABILITY_UNAVAILABLE}
 * by `z.literal`, and the only free value in the whole body is the registry's own
 * machine name. **That makes "this body carries no copy" a property the schema
 * itself enforces**, rather than a convention a reviewer has to police.
 *
 * ⚠️ `message` IS PINNED RATHER THAN ABSENT, AND THE ENVELOPE IS WHY. Every
 * `HttpException` renders a `message` — omit the key and Nest derives one from the
 * exception's class name (`"Service Unavailable Exception"`), which is English
 * prose nobody chose and nobody can change from here. Repeating the code is the
 * only value that is deliberate, non-empty, and carries no display decision; a
 * generic client that renders `error.message` then shows a token, which reads as
 * "map me" instead of as a sentence somebody wrote for a user.
 *
 * ⚠️ AND NO READINESS VALUE TRAVELS. `Readiness` has three members and exactly one
 * of them refuses — `degraded` proceeds, by design, and `ready` never reaches
 * here. A field carrying the same value on every response is not documentation, it
 * is an invitation to branch on the two values that never arrive. The code already
 * says which state this is.
 */
export const CAPABILITY_UNAVAILABLE = "CAPABILITY_UNAVAILABLE";

/**
 * What a client reads: the registry's machine name for the feature that is off.
 *
 * Safe to serve — it is the same string `/api/capabilities` publishes, and a
 * caller that reached this route already knows the feature exists. The name maps
 * to whatever the client wants to show; `requires` never travels, here or
 * anywhere else a customer can read.
 */
export const CapabilityUnavailableDetailsSchema = z.object({
  capability: PublicCapabilityStatusSchema.shape.name
});

export type CapabilityUnavailableDetails = z.infer<typeof CapabilityUnavailableDetailsSchema>;

/**
 * The payload a guard hands `ServiceUnavailableException`.
 *
 * Flat, because `HttpExceptionFilter` folds every key it has no envelope slot for
 * into `error.details` — so `capability` lands where
 * {@link CapabilityUnavailableErrorSchema} says it does, and the top-level `code`
 * is what makes that filter render the envelope on an internal route as well as
 * under the v1 prefix.
 */
export const CapabilityUnavailablePayloadSchema = z.object({
  code: z.literal(CAPABILITY_UNAVAILABLE),
  message: z.literal(CAPABILITY_UNAVAILABLE),
  capability: PublicCapabilityStatusSchema.shape.name
});

export type CapabilityUnavailablePayload = z.infer<typeof CapabilityUnavailablePayloadSchema>;

/**
 * What lands on the wire, once `HttpExceptionFilter` has rendered the payload.
 *
 * 🔑 THE TWO SCHEMAS ARE DECLARED SEPARATELY BECAUSE THEY ARE DIFFERENT SHAPES,
 * AND THE FILTER BETWEEN THEM IS NOT THIS PACKAGE'S TO ASSUME. Deriving one from
 * the other would encode a guess about `detailsOf`'s folding rule; asserting the
 * real filter's output against this schema — which
 * `requires-capability.guard.spec.ts` does over HTTP — proves the same thing and
 * would fail if that rule ever moved.
 */
export const CapabilityUnavailableErrorSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.literal(CAPABILITY_UNAVAILABLE),
    message: z.literal(CAPABILITY_UNAVAILABLE),
    details: CapabilityUnavailableDetailsSchema
  })
});

export type CapabilityUnavailableError = z.infer<typeof CapabilityUnavailableErrorSchema>;
